import { Injectable } from '@angular/core';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { environment } from '../../environments/environment';
import { LoginResponse, ValidationResponse } from '../models/user.model';
import { TOAST_MESSAGES } from '../constants/app.constants';
import { SessionExpiryService } from './session-expiry.service';

export interface BulkDeactivateUserError {
  row?: number;
  document_type?: string;
  document_number?: string;
  reason?: string;
  code?: string;
  message?: string;
}

export interface BulkDeactivateUsersResponse {
  deactivated_count: number;
  invalid_count: number;
  message?: string;
  error?: string;
  errors: BulkDeactivateUserError[];
}

export type DeactivateJobStatus = 'processing' | 'completed' | 'completed_with_errors' | 'failed';

export type QuotaPaymentRowStatus = 'PAID' | 'PARTIAL' | 'SKIPPED' | 'ERROR';

export interface QuotaPaymentRowResult {
  row: number;
  lending_code: string;
  quota_number: number | null;
  id_card_number: string;
  status: QuotaPaymentRowStatus;
  code: string;
  message: string;
  amount_paid: number | null;
  amount_rest: number | null;
}

export interface QuotaPaymentJobLog {
  id: string;
  status: DeactivateJobStatus;
  file_name?: string | null;
  total_rows: number;
  total_batches: number;
  batches_processed: number;
  paid_count: number;
  partial_count: number;
  skipped_count: number;
  error_count: number;
  invoice_error_count: number;
  invoice_generation_active: boolean;
  phase: QuotaPaymentJobPhase;
  documents_total: number;
  documents_processed: number;
  results_detail?: QuotaPaymentRowResult[] | null;
  message?: string | null;
  finished_at: string | null;
  updated_at: string;
}

export type QuotaPaymentJobPhase = 'payments' | 'documents' | 'done';

// Columnas de progreso: results_detail y pending_rows pueden pesar varios MB
const QUOTA_PAYMENT_JOB_PROGRESS_COLUMNS = 'id, status, phase, file_name, total_rows, total_batches, batches_processed, '
  + 'paid_count, partial_count, skipped_count, error_count, invoice_error_count, invoice_generation_active, '
  + 'documents_total, documents_processed, message, finished_at, updated_at';

export interface DeactivateJobLog {
  id: string;
  status: DeactivateJobStatus;
  business_id?: number;
  total_rows: number;
  total_batches: number;
  batches_processed: number;
  deactivated_count: number;
  skipped_count: number;
  error_count: number;
  errors_detail?: BulkDeactivateUserError[];
  metadata?: {
    business_id?: number;
    business_name?: string;
    [key: string]: any;
  };
  finished_at: string | null;
  message?: string | null;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class SupabaseService {
  private supabase: SupabaseClient;
  private refreshInFlight: Promise<boolean> | null = null;

  readonly authAwareFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await fetch(input, init);

    // Las peticiones al propio endpoint de auth (login, refresh, logout) no deben pasar
    // por este interceptor. Si lo hicieran, un refresh token vencido dispararía aquí un
    // nuevo tryRefreshSession() que esperaría la misma promesa que esta petición de
    // refresh todavía está resolviendo: un deadlock que congela la página.
    if (this.isAuthEndpointRequest(input)) {
      return response;
    }

    await this.handleAuthResponseIfNeeded(response);
    return response;
  };

  private isAuthEndpointRequest(input: RequestInfo | URL): boolean {
    const url = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;
    return url.includes('/auth/v1/');
  }

  constructor(private sessionExpiryService: SessionExpiryService) {
    this.supabase = createClient(
      environment.supabase.url,
      environment.supabase.anonKey,
      {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: false
        },
        global: {
          fetch: this.authAwareFetch
        }
      }
    );

    this.sessionExpiryService.registerSignOutHandler(async () => {
      await this.supabase.auth.signOut();
    });
  }

  get client(): SupabaseClient {
    return this.supabase;
  }

  get supabaseUrl(): string {
    return environment.supabase.url;
  }

  get supabaseKey(): string {
    return environment.supabase.anonKey;
  }

  async validateEmailAndLogin(email: string): Promise<ValidationResponse> {
    const { data: userData, error: userError } = await this.supabase
      .from('user')
      .select('id, first_name, last_name, user_type_id, has_password, email')
      .eq('email', email)
      .maybeSingle();

    if (userError) {
      return {
        success: false,
        message: 'Error al consultar la base de datos',
        user: undefined
      };
    }

    if (!userData) {
      return {
        success: false,
        message: TOAST_MESSAGES.USER_NOT_FOUND,
        user: undefined
      };
    }

    return {
      success: true,
      message: 'Usuario válido',
      user: undefined,
      userType: userData.user_type_id,
      hasPassword: userData.has_password,
      userInfo: userData
    };
  }

  // Tipo de flujo hilado desde el query param `type` de la ruta
  // (code-sent/verify-code/reset-password) hasta las llamadas a los Edge
  // Functions propios, para que el backend sepa si debe crear auth.users
  // (password_create) o exigir que ya exista (password_reset).
  private otpPurpose(type: 'create' | 'reset'): 'password_create' | 'password_reset' {
    return type === 'reset' ? 'password_reset' : 'password_create';
  }

  private async callOtpFunction<T = any>(functionName: string, body: object): Promise<T> {
    const functionUrl = `${environment.supabase.url}/functions/v1/${functionName}`;
    const response = await this.authAwareFetch(functionUrl, {
      method: 'POST',
      headers: {
        // Todavía no hay sesión en estos flujos (el usuario recién está
        // probando su email) — se manda la anon key, que el gateway de
        // Edge Functions exige como JWT válido aunque la función interna
        // no la use para identificar al usuario.
        'Authorization': `Bearer ${environment.supabase.anonKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    const responseText = await response.text();
    let data: any = null;
    try {
      data = responseText ? JSON.parse(responseText) : null;
    } catch {
      console.warn('[Supabase] No se pudo parsear la respuesta como JSON:', responseText);
    }
    return (data ?? { success: false, message: `Error del servidor: ${response.status}` }) as T;
  }

  async sendOTPForPasswordCreation(email: string): Promise<LoginResponse> {
    const data = await this.callOtpFunction('send-email-login-otp', {
      email,
      purpose: this.otpPurpose('create')
    });

    if (!data.success) {
      console.error('[Supabase] sendOTPForPasswordCreation error:', data);
      return { success: false, message: this.resolveOtpErrorMessage(data) };
    }

    return {
      success: true,
      message: `Código de verificación enviado.`
    };
  }

  async verifyOTP(email: string, otp: string, type: 'create' | 'reset' = 'create'): Promise<LoginResponse> {
    const verifyData = await this.callOtpFunction('verify-email-login-otp', {
      email,
      otp_code: otp,
      purpose: this.otpPurpose(type)
    });

    if (!verifyData.success) {
      return { success: false, message: this.resolveOtpErrorMessage(verifyData) };
    }

    // Canjear el token interno (nunca visto por el usuario) por una sesión real.
    const { data, error } = await this.supabase.auth.verifyOtp({
      email,
      token: verifyData.token,
      type: 'magiclink'
    });

    if (error || !data.session) {
      console.error('[Supabase] Error canjeando token de sesión:', error);
      return {
        success: false,
        message: 'Error al verificar el código. Intenta nuevamente.'
      };
    }

    if (data.user?.id) {
      const { data: linkResult, error: linkError } = await this.supabase.rpc(
        'user__link_pending_to_auth_by_email',
        { p_email: email, p_auth_user_id: data.user.id }
      );

      if (linkError || !linkResult?.success) {
        console.error('Error vinculando usuario:', linkError || linkResult?.message);
        return {
          success: false,
          message: linkResult?.message || 'Error al vincular el usuario. Intenta nuevamente.'
        };
      }
    }

    return {
      success: true,
      message: 'Código verificado exitosamente',
      session: data.session
    };
  }

  async resendOTP(email: string, type: 'create' | 'reset' = 'create'): Promise<LoginResponse> {
    return type === 'reset'
      ? this.sendOTPForPasswordReset(email)
      : this.sendOTPForPasswordCreation(email);
  }

  private resolveOtpErrorMessage(data: { error_code?: string; message?: string }): string {
    switch (data.error_code) {
      case 'RATE_LIMITED':
        return 'Demasiados intentos. Espera un minuto antes de volver a intentarlo.';
      case 'USER_NOT_FOUND':
        return 'El correo ingresado no está registrado en el sistema.';
      case 'OTP_EXPIRED':
        return 'El código ha expirado. Solicita un nuevo código.';
      case 'OTP_INVALID':
        return 'Código incorrecto. Verifica e intenta nuevamente.';
      case 'OTP_NOT_FOUND':
        return 'No hay un código pendiente. Solicita uno nuevo.';
      case 'OTP_MAX_ATTEMPTS':
        return 'Superaste el número de intentos permitidos. Solicita un nuevo código.';
      default:
        return data.message || 'No se pudo procesar el código. Intenta nuevamente luego de un minuto.';
    }
  }

  async sendOTPForPasswordReset(email: string): Promise<LoginResponse> {
    const data = await this.callOtpFunction('send-email-login-otp', {
      email,
      purpose: this.otpPurpose('reset')
    });

    if (!data.success) {
      console.error('[Supabase] sendOTPForPasswordReset error:', data);
      return { success: false, message: this.resolveOtpErrorMessage(data) };
    }

    return {
      success: true,
      message: `Código de verificación enviado.`
    };
  }

  async setPassword(email: string, password: string): Promise<LoginResponse> {
    try {
      const { data, error } = await this.supabase.auth.updateUser({
        password: password
      });

      if (error) {
        if (error.code === 'same_password') {
          return {
            success: false,
            message: 'Esta contraseña ya ha sido utilizada anteriormente.'
          };
        }

        return {
          success: false,
          message: `Error al establecer contraseña: ${error.message}`
        };
      }
    } catch (exception: any) {
      if (exception?.code === 'same_password' || exception?.message?.includes('same password')) {
        return {
          success: false,
          message: 'Esta contraseña ya ha sido utilizada anteriormente.'
        };
      }

      return {
        success: false,
        message: 'Error inesperado al establecer la contraseña. Por favor, intenta nuevamente.'
      };
    }

    const { data: { user }, error: getUserError } = await this.supabase.auth.getUser();

    if (getUserError || !user) {
      return {
        success: false,
        message: 'Error al obtener información del usuario autenticado'
      };
    }

    // El id ya fue sincronizado en verifyOTP, solo actualizamos has_password
    const { error: updateError } = await this.supabase
      .from('user')
      .update({
        has_password: true,
        updated_at: new Date().toISOString()
      })
      .eq('email', email);

    if (updateError) {
      return {
        success: false,
        message: 'Contraseña establecida pero error al actualizar estado del usuario'
      };
    }

    await this.supabase.auth.signOut();

    return {
      success: true,
      message: 'Contraseña establecida exitosamente'
    };
  }

  async signInWithPassword(email: string, password: string): Promise<LoginResponse> {
    const { data, error } = await this.supabase.auth.signInWithPassword({
      email: email,
      password: password
    });

    if (error) {
      if (error.message?.includes('Invalid login credentials')) {
        return {
          success: false,
          message: 'Contraseña incorrecta. Por favor verifica e intenta nuevamente.'
        };
      }

      return {
        success: false,
        message: 'Error al iniciar sesión. Intenta nuevamente.'
      };
    }

    const { data: userData, error: userError } = await this.supabase
      .from('user')
      .select('id, first_name, last_name, user_type_id, email, has_password')
      .eq('email', email)
      .maybeSingle();

    return {
      success: true,
      message: 'Inicio de sesión exitoso',
      session: data.session,
      user: data.user,
      userInfo: userData || undefined
    };
  }

  async getUserProfile(): Promise<any> {
    const { data, error } = await this.supabase.rpc('user__get_profile');

    if (error) {
      throw error;
    }

    return data;
  }

  async checkUserDuplicates(params: {
    userTypeId: number;
    idCardTypeId: number;
    idCardNumber?: string;
    mobilePhone?: string;
    email?: string;
    userId?: string;
  }): Promise<{
    has_duplicates: boolean;
    document_exists: boolean;
    phone_exists: boolean;
    email_exists: boolean;
  }> {
    const rpcParams: any = {
      p_user_type_id: params.userTypeId,
      p_id_card_type_id: params.idCardTypeId,
      p_id_card_number: params.idCardNumber || null,
      p_mobile_phone: params.mobilePhone || null,
      p_email: params.email || null,
      p_exclude_user_id: params.userId ? String(params.userId).trim() : null
    };

    const { data, error } = await this.supabase.rpc('user__check_duplicates', rpcParams);

    if (error) {
      console.error('[SupabaseService] Error en checkUserDuplicates:', error);
      throw error;
    }

    return data;
  }

  async checkBusinessDuplicates(params: {
    tin?: string;
    name?: string;
    businessId?: number;
  }): Promise<{
    tin_exists: boolean;
    name_exists: boolean;
  }> {
    const { data, error } = await this.supabase.rpc('business__check_duplicates', {
      p_tin: params.tin || null,
      p_name: params.name || null,
      p_business_id: params.businessId || null
    });

    if (error) {
      console.error('Error checking business duplicates:', error);
      throw error;
    }

    return data;
  }

  async getUserByIdCard(idCardTypeId: number, idCardNumber: string): Promise<any> {
    const { data, error } = await this.supabase.rpc('user__get_by_document', {
      p_id_card_type_id: idCardTypeId,
      p_id_card_number: idCardNumber
    });

    if (error) {
      console.error('Error buscando usuario por DNI:', error);
      throw error;
    }

    return data;
  }

  async getDeactivateJobLog(jobId: string): Promise<DeactivateJobLog> {
    const { data, error } = await this.supabase
      .from('deactivate_job_log')
      .select('*')
      .eq('id', jobId)
      .single();

    if (error) {
      console.error('Error obteniendo el estado del job de baja masiva:', error);
      throw error;
    }

    return data as DeactivateJobLog;
  }

  async getQuotaPaymentJobLog(jobId: string, includeDetail: boolean = false): Promise<QuotaPaymentJobLog> {
    const { data, error } = await this.supabase
      .from('quota_payment_job_log')
      .select(includeDetail ? `${QUOTA_PAYMENT_JOB_PROGRESS_COLUMNS}, results_detail` : QUOTA_PAYMENT_JOB_PROGRESS_COLUMNS)
      .eq('id', jobId)
      .single();

    if (error) {
      console.error('Error obteniendo el estado del job de pago masivo:', error);
      throw error;
    }

    return data as unknown as QuotaPaymentJobLog;
  }

  private async handleAuthResponseIfNeeded(response: Response): Promise<void> {
    if (response.status < 400) {
      return;
    }

    const responseText = await this.readResponseText(response);
    if (!this.isAuthErrorResponse(response.status, responseText)) {
      return;
    }

    // Un 401/403 puntual no siempre significa que la sesión terminó: el access token
    // pudo vencer (jwt_expiry) sin que el refresh en segundo plano llegara a tiempo
    // (p.ej. pestaña en segundo plano). Antes de cerrar sesión, intentamos refrescar
    // el token; solo si el refresh token también es inválido damos la sesión por expirada.
    const refreshed = await this.tryRefreshSession();
    if (refreshed) {
      return;
    }

    const authMessage = this.resolveAuthErrorMessage(responseText, response.statusText);
    await this.sessionExpiryService.handleSessionExpired(authMessage);
  }

  private async tryRefreshSession(): Promise<boolean> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.supabase.auth.refreshSession()
        .then(({ data, error }) => !error && !!data?.session)
        .catch(() => false)
        .finally(() => {
          this.refreshInFlight = null;
        });
    }

    return this.refreshInFlight;
  }

  private async readResponseText(response: Response): Promise<string> {
    try {
      return await response.clone().text();
    } catch (error) {
      console.warn('No se pudo leer la respuesta para detectar expiración de sesión:', error);
      return '';
    }
  }

  private resolveAuthErrorMessage(responseText: string, fallback: string): string {
    const parsedMessage = this.extractAuthMessageFromText(responseText);
    return parsedMessage || fallback || 'Tu sesión expiró. Vuelve a iniciar sesión.';
  }

  private isAuthErrorResponse(status: number, responseText: string): boolean {
    if (status === 401 || status === 403) {
      return true;
    }

    if (!responseText) {
      return false;
    }

    const lowerText = responseText.toLowerCase();
    return [
      'jwt expired',
      'jwt has expired',
      'invalid jwt',
      'not authorized',
      'unauthorized',
      'forbidden',
      'permission denied',
      'token expired',
      'session expired'
    ].some(pattern => lowerText.includes(pattern));
  }

  private extractAuthMessageFromText(responseText: string): string | null {
    if (!responseText) {
      return null;
    }

    const lowerText = responseText.toLowerCase();
    const authPatterns = [
      'jwt expired',
      'jwt has expired',
      'invalid jwt',
      'not authorized',
      'unauthorized',
      'forbidden',
      'permission denied',
      'token expired',
      'session expired'
    ];

    if (!authPatterns.some(pattern => lowerText.includes(pattern))) {
      return null;
    }

    try {
      const parsed = JSON.parse(responseText);
      return parsed?.message || parsed?.error || parsed?.msg || parsed?.error_description || null;
    } catch {
      return responseText;
    }
  }
  
}